/**
 * Кадр в раскадровке проекта: номер (за него тащат), заголовок и промпт
 * (правятся на месте), то, что уйдёт в модель после подстановки фактов,
 * слоты кадра с уровнем наследования, загрузка фото, генерация и лента.
 *
 * У кадра видео-проекта (`video`) — ещё поля клипа: камера, видео-промпт, переход и плашки клипов
 * по моделям. У обычного проекта карточка не меняется.
 */

import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ExternalLink, GripVertical, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router";

import { mediaUrl } from "@/lib/media";
import type { FieldResult, GenOptions, ProjectView, Shot, Slot } from "@/lib/project";
import { LEVEL_LABEL, postJson, qk } from "@/lib/project";
import type { TimelineShot } from "@/lib/video";
import { ShotVideoFields } from "@/components/video/ShotVideoFields";

import { GenPanel } from "./GenPanel";
import { GensFeed } from "./GensFeed";
import { InlineText } from "./InlineText";
import { ShotUpload } from "./ShotUpload";
import { Empty } from "./ui";

export function ShotCard({
  pid,
  shot,
  number,
  slots,
  options,
  seedAt,
  pending,
  uploadOpen,
  genOpen,
  armed,
  onToggleUpload,
  onToggleGen,
  onArm,
  onHandleDown,
  openVariant,
  onOpenVariant,
  video,
}: {
  pid: number;
  shot: Shot;
  number: number;
  slots: Slot[];
  options: GenOptions;
  seedAt: number;
  pending: number;
  uploadOpen: boolean;
  genOpen: boolean;
  armed: boolean;
  onToggleUpload: () => void;
  onToggleGen: () => void;
  onArm: () => void;
  onHandleDown: () => void;
  openVariant: number | null;
  onOpenVariant: (variantId: number) => void;
  /** Строка хронологии кадра — только у видео-проекта. */
  video?: TimelineShot | undefined;
}) {
  const client = useQueryClient();

  const saveField = async (field: "shot_title" | "shot_prompt", value: string) => {
    const result = await postJson<FieldResult>(`/api/projects/${pid}/shots/${shot.id}/field`, { field, value });
    client.setQueryData<ProjectView>(qk.project(pid), (old) =>
      old
        ? {
            ...old,
            shots: old.shots.map((s) =>
              s.id !== shot.id
                ? s
                : field === "shot_title"
                  ? { ...s, shot_title: result.value }
                  : { ...s, shot_prompt: result.value || null, prompt_resolved: result.prompt_resolved },
            ),
          }
        : old,
    );
    // Карточка кадра (история правок) живёт в своём кэше — пусть перечитается.
    void client.invalidateQueries({ queryKey: qk.shot(pid, shot.id) });
    return result.value;
  };

  return (
    <div className="flex gap-3">
      <div
        onMouseDown={onHandleDown}
        title="перетащите, чтобы изменить порядок"
        className="tnum flex h-8 w-9 flex-none cursor-grab items-center justify-center gap-0.5 rounded-[var(--radius-ui)] border border-border bg-bg text-[13px] font-semibold text-fg-muted select-none hover:border-accent hover:text-accent-text active:cursor-grabbing"
      >
        <GripVertical size={11} className="text-fg-faint" />
        {number}
      </div>

      <div className="grid min-w-0 flex-1 gap-1.5">
        <div className="flex items-start gap-2">
          <InlineText
            value={shot.shot_title}
            placeholder="без заголовка"
            onSave={(v) => saveField("shot_title", v)}
            className="min-w-0 flex-1 text-[13.5px] font-medium"
            annot="заголовок кадра"
          />
          <Link
            to={`/project/${pid}/shot/${shot.id}`}
            title="открыть кадр целиком"
            className="mt-0.5 grid size-6 flex-none place-items-center rounded-[var(--radius-ui)] text-fg-faint hover:bg-bg-hover hover:text-accent-text"
          >
            <ExternalLink size={13} />
          </Link>
        </div>

        <InlineText
          value={shot.shot_prompt}
          multiline
          placeholder="промпт не задан"
          onSave={(v) => saveField("shot_prompt", v)}
          className="text-[12.5px] text-fg-muted"
          annot="промпт кадра"
        />

        {/* Переменные вида {product_title} выглядят недоделкой, пока не видно,
            что из них получается: результат подстановки — когда он отличается. */}
        {shot.prompt_resolved ? (
          <div className="rounded-[var(--radius-ui)] border-l-2 border-accent bg-bg px-2 py-1 text-[11.5px] text-fg-faint">
            уйдёт в модель: {shot.prompt_resolved}
          </div>
        ) : null}

        {video ? <ShotVideoFields pid={pid} shot={video} /> : null}

        <div className="flex flex-wrap items-end gap-1.5">
          {shot.slots.map((r) => (
            <div
              key={r.slot_code}
              data-annot={`слот ${r.slot_code}`}
              title={`${r.slot_name} — ${LEVEL_LABEL[r.level]}`}
              className="w-[52px]"
            >
              <img
                src={mediaUrl(r.asset_path)}
                alt={r.slot_code}
                loading="lazy"
                className={clsx(
                  "aspect-[3/4] w-full rounded-[4px] border object-cover",
                  r.level === "shot" ? "border-accent" : "border-border",
                  r.level === "template" && "opacity-70",
                )}
              />
              <div
                className={clsx(
                  "truncate text-center text-[10px]",
                  r.level === "shot" ? "text-accent-text" : "text-fg-faint",
                )}
              >
                {r.slot_code.split(".")[1] ?? r.slot_code}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={onToggleUpload}
            title="добавить фотографии в кадр"
            className={clsx(
              "grid aspect-[3/4] w-[52px] place-items-center self-start rounded-[4px] border border-dashed",
              uploadOpen
                ? "border-accent text-accent-text"
                : "border-border-strong text-fg-faint hover:border-accent hover:text-accent-text",
            )}
          >
            <Plus size={16} />
          </button>
          <button
            type="button"
            onClick={onToggleGen}
            className={clsx(
              "ml-1 inline-flex h-8 items-center gap-1.5 self-center rounded-[var(--radius-ui)] border px-3 text-[12.5px]",
              genOpen
                ? "border-accent bg-accent-dim text-accent-text"
                : "border-border bg-bg hover:border-border-strong hover:bg-bg-hover",
            )}
          >
            <Sparkles size={13} />
            Генерация
          </button>
        </div>

        {/* Панель загрузки открывается по «+» и ею же взводится: пока она
            открыта, Ctrl-V кладёт картинку именно сюда. */}
        {uploadOpen ? (
          <ShotUpload
            pid={pid}
            sid={shot.id}
            freeSlots={shot.free_slots}
            allSlots={slots}
            armed={armed}
            onArm={onArm}
            onUploaded={() => void client.invalidateQueries({ queryKey: qk.project(pid) })}
          />
        ) : null}

        {!shot.slots.length ? <Empty>слоты не заполнены</Empty> : null}

        {genOpen ? <GenPanel pid={pid} sid={shot.id} inputs={shot.inputs} options={options} /> : null}

        <GensFeed
          pid={pid}
          sid={shot.id}
          seed={{ data: { gens: shot.gens, pending, error: "" }, at: seedAt }}
          openVariant={openVariant}
          onOpen={onOpenVariant}
        />
      </div>
    </div>
  );
}
